// 轮询管理器类
interface PollerOptions {
  taskId: string;
  pollFn: () => Promise<void>;
  interval?: number; // 轮询间隔，默认 5000ms
  maxRetries?: number; // 最大重试次数，-1 表示无限重试
}

interface PollerConfig {
  taskId: string;
  timerId?: number;
  retryCount: number;
}

export class PollingManager {
  private pollers = new Map<string, PollerConfig>(); 
  private readonly DEFAULT_INTERVAL = 5000;
  private readonly DEFAULT_MAX_RETRIES = -1; // 无限重试

  /**
   * 启动轮询
   */
  async start(options: PollerOptions): Promise<void> {
    const {
      taskId,
      pollFn,
      interval = this.DEFAULT_INTERVAL,
      maxRetries = this.DEFAULT_MAX_RETRIES
    } = options;

    // 防止重复启动
    if (this.pollers.has(taskId)) {
      console.warn(`Polling for taskId ${taskId} is already running`);
      return;
    }

    const config: PollerConfig = { taskId, retryCount: 0 };
    this.pollers.set(taskId, config);

    const poll = async () => {
      try {
        await pollFn();
        // 重置重试计数（成功时）
        config.retryCount = 0;
      } catch (error) {
        config.retryCount++;
        console.error(`Polling error for taskId ${taskId}:`, error);

        // 检查是否应该停止重试
        if (maxRetries !== -1 && config.retryCount > maxRetries) {
          console.error(`Max retries (${maxRetries}) reached for taskId ${taskId}`);
          this.stop(taskId);
          return;
        }
      }

      // 如果轮询还存在，继续安排下一次轮询
      if (this.pollers.has(taskId)) {
        config.timerId = window.setTimeout(poll, interval);
      }
    };

    // 立即执行第一次
    await poll();
  }

  /**
   * 停止指定任务的轮询
   */
  stop(taskId: string): void {
    const config = this.pollers.get(taskId);
    if (!config) return;

    if (config.timerId !== undefined) {
      window.clearTimeout(config.timerId);
    }
    this.pollers.delete(taskId);
  }

  /**
   * 停止所有轮询
   */
  stopAll(): void {
    this.pollers.forEach((config) => {
      if (config.timerId !== undefined) {
        window.clearTimeout(config.timerId);
      }
    });
    this.pollers.clear();
  }

  /**
   * 检查是否存在活跃的轮询
   */
  isPolling(taskId: string): boolean {
    return this.pollers.has(taskId);
  }

  /**
   * 获取当前轮询数量
   */
  getPollingCount(): number {
    return this.pollers.size;
  }

  /**
   * 获取所有活跃的轮询 taskId
   */
  getActiveTaskIds(): string[] {
    return Array.from(this.pollers.keys());
  }
}